import { useState } from 'react';
import { formatDate } from '../../../lib/format';
import { STATION_LABEL } from '@dru/types';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { AuditRow, CatalogueProtocolDetail, CursorPage } from '@dru/types';
import { TEST_CATEGORY_LABEL } from '@dru/types';
import { Alert, Button, Detail, DetailHeader, DetailSection, InfoTip, StatusBadge, TextSkeleton } from '@dru/ui';
import { useAuth } from '../../../lib/auth';
import { plural } from '../components/RetireAction';
import { ActivityTable, NotForYourRole } from './Audit';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { ProtocolDialog } from '../components/CatalogueDialogs';
import { useBreadcrumbTitle } from '../../../layouts/BreadcrumbContext';

/**
 * One protocol version: the battery, the hazards it answers, its cadence,
 * and every other version of the same code. Versions are never edited —
 * "New version" publishes the next one and supersedes this.
 */
export function ProtocolDetail() {
  if (!useAuth().permissions.has('protocol.read')) {
    return <NotForYourRole title="Protocol" what="The clinical catalogue is maintained by platform administrators." />;
  }
  return <ProtocolDetailPage />;
}

function ProtocolDetailPage() {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const protocol = useQuery<CatalogueProtocolDetail>(() => api.get(`/protocols/${id}`), [id]);
  const trail = useQuery<CursorPage<AuditRow>>(() => api.get(`/audit?entityType=Protocol&entityId=${id}&limit=50`), [id]);
  const [versioning, setVersioning] = useState(false);
  const p = protocol.data;
  useBreadcrumbTitle(p?.name);
  const canManage = useAuth().permissions.has('protocol.manage');
  const live = p?.versions.find((v) => v.isActive);

  return (
    <div className="space-y-5">
      <DetailHeader
        eyebrow="Protocol"
        title={p ? p.name : (protocol.error ? 'Protocol not found' : 'Loading…')}
        badges={p && <StatusBadge tone={p.isActive ? 'covered' : 'neutral'}>{p.isActive ? `Live · v${p.version}` : `Superseded · v${p.version}`}</StatusBadge>}
        subtitle={p && [p.code, `repeat every ${p.periodicityMonths} months`, `certificate valid ${p.certificateValidityMonths} months`, p.requiresConsent ? 'consent required' : 'no consent step'].join(' · ')}
        actions={p?.isActive && canManage && <Button onClick={() => setVersioning(true)}>Publish version {p.version + 1}</Button>}
      />
      {protocol.error && <Alert tone="danger">{protocol.error}</Alert>}
      {protocol.loading && !p && <TextSkeleton lines={6} />}
      {p && !p.isActive && (
        <Alert tone="info" title={`This is version ${p.version}, kept for the record`}>
          Examinations raised under it still resolve against this battery. New examinations use{' '}
          {live
            ? <Link to={`/admin/masters/protocols/${live.id}`} className="font-medium underline">version {live.version}</Link>
            : 'no version — none of this protocol is live'}.
        </Alert>
      )}
      {p && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 [&>*]:min-w-0">
          <DetailSection title={`Tests (${p.tests.length} · ${p.tests.filter((t) => t.isMandatory).length} required)`}>
            <ul className="divide-y divide-neutral-100">
              {p.tests.map((t) => (
                <li key={t.id} className="flex items-center justify-between gap-2 py-1.5 text-small">
                  <span className="min-w-0">
                    <Link to={`/admin/masters/tests/${t.id}`} className="inline-block py-1 text-primary-700 hover:underline">{t.name}</Link>
                    <span className="ml-2 font-mono text-caption text-neutral-500">{t.code}</span>
                    <span className="block text-caption text-neutral-500">{TEST_CATEGORY_LABEL[t.category as never] ?? t.category}{t.station ? ` · ${(STATION_LABEL[t.station as never] ?? t.station)}` : ''}</span>
                  </span>
                  <StatusBadge tone={t.isMandatory ? 'covered' : 'neutral'}>{t.isMandatory ? 'Required' : 'Optional'}</StatusBadge>
                </li>
              ))}
            </ul>
          </DetailSection>

          <div className="space-y-4">
            <DetailSection title={p.hazards.length ? `Applies to employees exposed to (${p.hazards.length})` : 'Applies to every employee'}>
              {p.hazards.length === 0 ? <p className="text-small text-neutral-500">Baseline protocol — applies to all employees regardless of specific hazard exposures.</p> : (
                <ul className="flex flex-wrap gap-1.5">
                  {p.hazards.map((h) => (
                    <li key={h.id}><Link to={`/admin/masters/hazards/${h.id}`} className="inline-flex min-h-[28px] max-md:min-h-10 items-center"><StatusBadge tone="neutral">{h.name}</StatusBadge></Link></li>
                  ))}
                </ul>
              )}
            </DetailSection>
            <DetailSection title="Schedule and validity">
              <div className="grid gap-3 sm:grid-cols-2">
                <Detail label="Repeat every" value={`${p.periodicityMonths} months`} />
                <Detail label="Certificate valid" value={`${p.certificateValidityMonths} months`} />
                <Detail label="Consent" value={p.requiresConsent ? 'Required before check-in' : 'Not required'} />
                <Detail label="Effective from" value={formatDate(p.effectiveFrom)} />
                <Detail label="Priced on rate cards" value={plural(p.rateCardLines, 'line')} />
              </div>
              {p.description && <p className="mt-3 text-small text-neutral-700">{p.description}</p>}
            </DetailSection>
          </div>

          <DetailSection
            phoneFolded
            title={`Versions of ${p.code} (${p.versions.length})`}
            className="lg:col-span-2"
            action={(
              <InfoTip label="About versions">
                Every examination raised under a version keeps resolving against that version's battery.
              </InfoTip>
            )}
          >
            <ul className="divide-y divide-neutral-100">
              {p.versions.map((v) => (
                <li key={v.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-small">
                  <span>
                    {v.id === p.id ? <span className="text-neutral-900">v{v.version} — {v.name} (this page)</span>
                      : <Link to={`/admin/masters/protocols/${v.id}`} className="inline-block py-1 text-primary-700 hover:underline">v{v.version} — {v.name}</Link>}
                    <span className="ml-2 text-caption text-neutral-500">{v.testCount} tests · from {formatDate(v.effectiveFrom)}</span>
                  </span>
                  <StatusBadge tone={v.isActive ? 'covered' : 'neutral'}>{v.isActive ? 'Live' : 'Superseded'}</StatusBadge>
                </li>
              ))}
            </ul>
          </DetailSection>

          <DetailSection title="History" className="lg:col-span-2">
            <ActivityTable rows={trail.data?.items ?? []} loading={trail.loading} error={trail.error} onRetry={trail.reload}
              emptyHint="Versions published and changes to this protocol appear here." />
          </DetailSection>
        </div>
      )}
      {p && versioning && (
        <ProtocolDialog base={p} onClose={() => setVersioning(false)}
          onDone={async () => {
            setVersioning(false);
            // The new version is the live one now; land on it.
            const fresh = await api.get<CatalogueProtocolDetail>(`/protocols/${id}`);
            const live = fresh.versions.find((v) => v.isActive);
            nav(live ? `/admin/masters/protocols/${live.id}` : `/admin/masters/protocols/${id}`, { replace: true });
            protocol.reload();
          }} />
      )}
    </div>
  );
}
